import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Send } from 'lucide-react';

const ChatComposer = ({ disabled, onSend }) => {
  const [text, setText] = useState('');
  const textareaRef = useRef(null);

  const canSend = useMemo(
    () => !disabled && String(text || '').trim().length > 0,
    [disabled, text]
  );

  const handleSubmit = useCallback(
    (e) => {
      e?.preventDefault?.();
      if (!canSend) return;
      onSend?.(text);
      setText('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.focus();
      }
    },
    [canSend, onSend, text]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (canSend) handleSubmit(e);
      }
    },
    [canSend, handleSubmit]
  );

  const handleInput = useCallback((e) => {
    // Auto-resize: max 6 líneas (~128px)
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
  }, []);

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2.5">
      {/* Textarea */}
      <div className="relative flex-1">
        <textarea
          ref={textareaRef}
          id="ai-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          disabled={disabled}
          rows={1}
          placeholder="Pregunta sobre contratos, pagos, cotizaciones…"
          className={[
            'w-full resize-none rounded-xl border bg-white px-4 py-3',
            'min-h-[48px] max-h-32 overflow-y-auto',
            'text-sm text-slate-900 placeholder:text-slate-400',
            'border-slate-200 focus:border-[#007D6E] focus:outline-none focus:ring-2 focus:ring-[#007D6E]/15',
            'disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed',
            'dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500',
            'dark:focus:border-[#007D6E] dark:focus:ring-[#007D6E]/20',
            'transition-colors duration-150',
          ].join(' ')}
        />
      </div>

      {/* Send button */}
      <div className="flex flex-col items-center gap-1 pb-0">
        <button
          type="submit"
          disabled={!canSend}
          title="Enviar (Enter)"
          className={[
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
            'bg-[#007D6E] text-white hover:bg-[#006358]',
            'disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-[#007D6E]/30',
          ].join(' ')}
        >
          <Send className="h-4 w-4" />
        </button>
        <span className="text-[9px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
          Enter · enviar
        </span>
      </div>
    </form>
  );
};

export default ChatComposer;
